import { useCallback } from 'react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Tabs } from '@components/ui/Tabs/Tabs';
import { BellIcon, HardHatIcon, TagBadgeIcon, UsersIcon } from '@/utils/icons';
import { TagsTab } from './TagsTab/TagsTab';
import { ProfessionsTab } from './ProfessionsTab/ProfessionsTab';
import { ManagementTab } from './ManagementTab/ManagementTab';
import { UsersTab } from './UsersTab/UsersTab';
import { AlertsHubTab } from './AlertsHubTab/AlertsHubTab';
import styles from './AdminPage.module.css';

type AdminTabId = 'tags' | 'users' | 'management' | 'professions' | 'alertsHub';

const ADMIN_TABS: { id: AdminTabId; labelKey: string; Icon: ComponentType<{ size?: number }> }[] = [
  { id: 'tags',        labelKey: 'admin.tabs.tags',        Icon: TagBadgeIcon },
  { id: 'users',       labelKey: 'admin.tabs.users',       Icon: UsersIcon },
  { id: 'management',  labelKey: 'admin.tabs.management',  Icon: HardHatIcon },
  { id: 'professions', labelKey: 'admin.tabs.professions', Icon: HardHatIcon },
  { id: 'alertsHub',   labelKey: 'admin.tabs.alertsHub',   Icon: BellIcon },
];

const DEFAULT_TAB: AdminTabId = 'tags';

function isAdminTabId(value: string | null): value is AdminTabId {
  return ADMIN_TABS.some((tab) => tab.id === value);
}

export function AdminPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const active: AdminTabId = isAdminTabId(tabParam) ? tabParam : DEFAULT_TAB;

  const handleTabChange = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <Tabs.Root value={active} onValueChange={handleTabChange} className={styles.root}>
      <Tabs.List>
        {ADMIN_TABS.map((tab) => (
          <Tabs.Tab key={tab.id} value={tab.id}>
            <tab.Icon size={16} />
            {t(tab.labelKey)}
          </Tabs.Tab>
        ))}
        <Tabs.Indicator />
      </Tabs.List>
      <Tabs.Panel value="tags">
        <TagsTab />
      </Tabs.Panel>
      <Tabs.Panel value="users">
        <UsersTab />
      </Tabs.Panel>
      <Tabs.Panel value="management">
        <ManagementTab />
      </Tabs.Panel>
      <Tabs.Panel value="professions">
        <ProfessionsTab />
      </Tabs.Panel>
      <Tabs.Panel value="alertsHub">
        <AlertsHubTab />
      </Tabs.Panel>
    </Tabs.Root>
  );
}